import React, { useState, useEffect } from "react";
import ChatSidebar from "./ChatSidebar";
import { handleDeleteChat } from "../utils/chatSessionHandlers";

const ensureBackendAwake = async () => {
  try {
    const res = await fetch(`${"https://vintariai-backend-1.onrender.com"}/ping`);
    return res.ok;
  } catch {
    return false;
  }
};

export default function SidebarContainer({
  user,
  supabase,
  sidebarOpen,
  setSidebarOpen,
  chatList,
  currentChatId,
  setCurrentChatId,
  setConversation,
  fetchChatSessions,
  fetchChatHistory,
}) {
  const [wakingUp, setWakingUp] = useState(false);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;


  const withBackendWakeCheck = async (onAwake) => {
    let timeout = setTimeout(() => setWakingUp(true), 1000);
    const awake = await ensureBackendAwake();
    clearTimeout(timeout);
    setWakingUp(false);

    if (awake) {
      await onAwake();
    } else {
      console.warn("⏳ Backend sleeping. Retrying in 3 seconds...");
      setWakingUp(true);
      setTimeout(async () => {
        setWakingUp(false);
        await onAwake();
      }, 3000);
    }
  };

  // This useEffect hook is correct and does not need changes.
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === "visible" && currentChatId) {
        await withBackendWakeCheck(() => fetchChatHistory(currentChatId));
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [currentChatId, fetchChatHistory]);

  return (

    <div>
      {wakingUp && (
        <div className="text-center text-xs text-white/70 italic px-4 py-2 animate-pulse bg-black/20">
          🔄 Waking up server… please wait.
        </div>
      )}
      
      <ChatSidebar
      user={user}
      sidebarOpen={sidebarOpen} 
        chatList={chatList}
        currentChatId={currentChatId}
        onSelectChat={async (id) => {
          setCurrentChatId(id);
          await withBackendWakeCheck(() => fetchChatHistory(id));
          if (isMobile) setSidebarOpen(false);
        }}
        onNewChat={async () => {
          if (!user) return alert("You must be logged in to start a new chat.");
          try {
            const token = (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) return alert("No auth token found.");

            const res = await fetch(`${"https://vintariai-backend-1.onrender.com"}/api/new-chat`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to create new chat.");

            const newId = data.chat.id;
            setCurrentChatId(newId);
            setConversation({ id: newId, messages: [] });
            await fetchChatSessions();
            if (isMobile) setSidebarOpen(false);
          } catch (err) {
            console.error("❌ Network or fetch error:", err);
            alert("Something went wrong creating the chat.");
          }
        }}
 onRenameChat={async (chatId, newTitle) => {
   try {
     const { data: { session } } = await supabase.auth.getSession();
     const token = session?.access_token;
     if (!token) return alert("No auth token found.");

     const res = await fetch(
       `${"https://vintariai-backend-1.onrender.com"}/api/chats/${chatId}`, 
       {
         method: "PATCH",
         headers: {
           "Content-Type": "application/json",
           Authorization: `Bearer ${token}`,
         },
         body: JSON.stringify({ title: newTitle.trim() }),
       }
     );

     if (!res.ok) {
       const err = await res.json();
       return alert(err.error || "Failed to rename chat.");
     }

     // Refresh the sidebar list so the new title shows up
     await fetchChatSessions();
   } catch (err) {
     console.error("Rename error:", err);
     alert("Something went wrong while renaming.");
   }
 }}
        onDeleteChat={async (chatIdToDelete) => {
          // ✅ CORRECTED AND SIMPLIFIED DELETION LOGIC
          const nextId = await handleDeleteChat({
            supabase,
            chatList,
            chatIdToDelete,
          });

          // After deleting, just fetch the new list of sessions.
          // The useChatSession hook will automatically handle switching to the correct chat.
          await fetchChatSessions();
          
          // Set the new current ID. The fetchChatHistory will be triggered by other effects.
          setCurrentChatId(nextId);
          
          if (!nextId) {
            // If no chats are left, explicitly clear the conversation.
            setConversation({ id: null, messages: [] });
          } else {
            // Otherwise, fetch the history for the new chat
            fetchChatHistory(nextId);
          }

          if (isMobile) setSidebarOpen(false);
        }}
        setSidebarOpen={setSidebarOpen}
      />
    </div>
 
  );
}